import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Hash, Upload, Copy, Check, X, FileText, Shield, 
  RefreshCw, AlertTriangle, CheckCircle, Info, Sparkles
} from 'lucide-react';

// Web Crypto API hash functions
const generateHash = async (data, algorithm) => {
  const encoder = new TextEncoder();
  const dataBuffer = typeof data === 'string' ? encoder.encode(data) : data;
  
  let hashAlgo;
  switch (algorithm) {
    case 'SHA-256':
      hashAlgo = 'SHA-256';
      break;
    case 'SHA-512':
      hashAlgo = 'SHA-512';
      break;
    case 'SHA-1':
      hashAlgo = 'SHA-1';
      break;
    case 'MD5':
      // MD5 is not supported by Web Crypto API, use a simple implementation
      return md5(dataBuffer);
    default:
      hashAlgo = 'SHA-256';
  }
  
  const hashBuffer = await crypto.subtle.digest(hashAlgo, dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Simple MD5 implementation (for demo - in production use a proper library)
const md5 = (data) => {
  // Convert to string if needed
  let str = '';
  if (data instanceof Uint8Array) {
    str = Array.from(data).map(b => String.fromCharCode(b)).join('');
  } else {
    str = data;
  }
  
  function rotateLeft(x, n) {
    return (x << n) | (x >>> (32 - n));
  }

  function addUnsigned(x, y) {
    const x8 = x & 0x80000000;
    const y8 = y & 0x80000000;
    const x4 = x & 0x40000000;
    const y4 = y & 0x40000000;
    const result = (x & 0x3FFFFFFF) + (y & 0x3FFFFFFF);
    if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
    if (x4 | y4) {
      if (result & 0x40000000) return result ^ 0xC0000000 ^ x8 ^ y8;
      else return result ^ 0x40000000 ^ x8 ^ y8;
    }
    return result ^ x8 ^ y8;
  }

  function f(x, y, z) { return (x & y) | ((~x) & z); }
  function g(x, y, z) { return (x & z) | (y & (~z)); }
  function h(x, y, z) { return x ^ y ^ z; }
  function i(x, y, z) { return y ^ (x | (~z)); }

  function ff(a, b, c, d, x, s, ac) {
    a = addUnsigned(a, addUnsigned(addUnsigned(f(b, c, d), x), ac));
    return addUnsigned(rotateLeft(a, s), b);
  }

  function gg(a, b, c, d, x, s, ac) {
    a = addUnsigned(a, addUnsigned(addUnsigned(g(b, c, d), x), ac));
    return addUnsigned(rotateLeft(a, s), b);
  }

  function hh(a, b, c, d, x, s, ac) {
    a = addUnsigned(a, addUnsigned(addUnsigned(h(b, c, d), x), ac));
    return addUnsigned(rotateLeft(a, s), b);
  }

  function ii(a, b, c, d, x, s, ac) {
    a = addUnsigned(a, addUnsigned(addUnsigned(i(b, c, d), x), ac));
    return addUnsigned(rotateLeft(a, s), b);
  }

  function convertToWordArray(str) {
    const wordCount = (((str.length + 8) - ((str.length + 8) % 64)) / 64 + 1) * 16;
    const wordArray = new Array(wordCount - 1);
    let bytePosition = 0, byteCount = 0;
    while (byteCount < str.length) {
      const wordPosition = (byteCount - (byteCount % 4)) / 4;
      bytePosition = (byteCount % 4) * 8;
      wordArray[wordPosition] = (wordArray[wordPosition] | (str.charCodeAt(byteCount) << bytePosition));
      byteCount++;
    }
    const wordPosition = (byteCount - (byteCount % 4)) / 4;
    bytePosition = (byteCount % 4) * 8;
    wordArray[wordPosition] = wordArray[wordPosition] | (0x80 << bytePosition);
    wordArray[wordCount - 2] = str.length << 3;
    wordArray[wordCount - 1] = str.length >>> 29;
    return wordArray;
  }

  function wordToHex(value) {
    let hex = '', temp;
    for (let i = 0; i <= 3; i++) {
      temp = (value >>> (i * 8)) & 255;
      hex += ('0' + temp.toString(16)).slice(-2);
    }
    return hex;
  }

  const x = convertToWordArray(str);
  let a = 0x67452301, b = 0xEFCDAB89, c = 0x98BADCFE, d = 0x10325476;

  const S11 = 7, S12 = 12, S13 = 17, S14 = 22;
  const S21 = 5, S22 = 9, S23 = 14, S24 = 20;
  const S31 = 4, S32 = 11, S33 = 16, S34 = 23;
  const S41 = 6, S42 = 10, S43 = 15, S44 = 21;

  for (let k = 0; k < x.length; k += 16) {
    const AA = a, BB = b, CC = c, DD = d;
    a = ff(a, b, c, d, x[k + 0], S11, 0xD76AA478);
    d = ff(d, a, b, c, x[k + 1], S12, 0xE8C7B756);
    c = ff(c, d, a, b, x[k + 2], S13, 0x242070DB);
    b = ff(b, c, d, a, x[k + 3], S14, 0xC1BDCEEE);
    a = ff(a, b, c, d, x[k + 4], S11, 0xF57C0FAF);
    d = ff(d, a, b, c, x[k + 5], S12, 0x4787C62A);
    c = ff(c, d, a, b, x[k + 6], S13, 0xA8304613);
    b = ff(b, c, d, a, x[k + 7], S14, 0xFD469501);
    a = ff(a, b, c, d, x[k + 8], S11, 0x698098D8);
    d = ff(d, a, b, c, x[k + 9], S12, 0x8B44F7AF);
    c = ff(c, d, a, b, x[k + 10], S13, 0xFFFF5BB1);
    b = ff(b, c, d, a, x[k + 11], S14, 0x895CD7BE);
    a = ff(a, b, c, d, x[k + 12], S11, 0x6B901122);
    d = ff(d, a, b, c, x[k + 13], S12, 0xFD987193);
    c = ff(c, d, a, b, x[k + 14], S13, 0xA679438E);
    b = ff(b, c, d, a, x[k + 15], S14, 0x49B40821);
    a = gg(a, b, c, d, x[k + 1], S21, 0xF61E2562);
    d = gg(d, a, b, c, x[k + 6], S22, 0xC040B340);
    c = gg(c, d, a, b, x[k + 11], S23, 0x265E5A51);
    b = gg(b, c, d, a, x[k + 0], S24, 0xE9B6C7AA);
    a = gg(a, b, c, d, x[k + 5], S21, 0xD62F105D);
    d = gg(d, a, b, c, x[k + 10], S22, 0x02441453);
    c = gg(c, d, a, b, x[k + 15], S23, 0xD8A1E681);
    b = gg(b, c, d, a, x[k + 4], S24, 0xE7D3FBC8);
    a = gg(a, b, c, d, x[k + 9], S21, 0x21E1CDE6);
    d = gg(d, a, b, c, x[k + 14], S22, 0xC33707D6);
    c = gg(c, d, a, b, x[k + 3], S23, 0xF4D50D87);
    b = gg(b, c, d, a, x[k + 8], S24, 0x455A14ED);
    a = gg(a, b, c, d, x[k + 13], S21, 0xA9E3E905);
    d = gg(d, a, b, c, x[k + 2], S22, 0xFCEFA3F8);
    c = gg(c, d, a, b, x[k + 7], S23, 0x676F02D9);
    b = gg(b, c, d, a, x[k + 12], S24, 0x8D2A4C8A);
    a = hh(a, b, c, d, x[k + 5], S31, 0xFFFA3942);
    d = hh(d, a, b, c, x[k + 8], S32, 0x8771F681);
    c = hh(c, d, a, b, x[k + 11], S33, 0x6D9D6122);
    b = hh(b, c, d, a, x[k + 14], S34, 0xFDE5380C);
    a = hh(a, b, c, d, x[k + 1], S31, 0xA4BEEA44);
    d = hh(d, a, b, c, x[k + 4], S32, 0x4BDECFA9);
    c = hh(c, d, a, b, x[k + 7], S33, 0xF6BB4B60);
    b = hh(b, c, d, a, x[k + 10], S34, 0xBEBFBC70);
    a = hh(a, b, c, d, x[k + 13], S31, 0x289B7EC6);
    d = hh(d, a, b, c, x[k + 0], S32, 0xEAA127FA);
    c = hh(c, d, a, b, x[k + 3], S33, 0xD4EF3085);
    b = hh(b, c, d, a, x[k + 6], S34, 0x04881D05);
    a = hh(a, b, c, d, x[k + 9], S31, 0xD9D4D039);
    d = hh(d, a, b, c, x[k + 12], S32, 0xE6DB99E5);
    c = hh(c, d, a, b, x[k + 15], S33, 0x1FA27CF8);
    b = hh(b, c, d, a, x[k + 2], S34, 0xC4AC5665);
    a = ii(a, b, c, d, x[k + 0], S41, 0xF4292244);
    d = ii(d, a, b, c, x[k + 7], S42, 0x432AFF97);
    c = ii(c, d, a, b, x[k + 14], S43, 0xAB9423A7);
    b = ii(b, c, d, a, x[k + 5], S44, 0xFC93A039);
    a = ii(a, b, c, d, x[k + 12], S41, 0x655B59C3);
    d = ii(d, a, b, c, x[k + 3], S42, 0x8F0CCC92);
    c = ii(c, d, a, b, x[k + 10], S43, 0xFFEFF47D);
    b = ii(b, c, d, a, x[k + 1], S44, 0x85845DD1);
    a = ii(a, b, c, d, x[k + 8], S41, 0x6FA87E4F);
    d = ii(d, a, b, c, x[k + 15], S42, 0xFE2CE6E0);
    c = ii(c, d, a, b, x[k + 6], S43, 0xA3014314);
    b = ii(b, c, d, a, x[k + 13], S44, 0x4E0811A1);
    a = ii(a, b, c, d, x[k + 4], S41, 0xF7537E82);
    d = ii(d, a, b, c, x[k + 11], S42, 0xBD3AF235);
    c = ii(c, d, a, b, x[k + 2], S43, 0x2AD7D2BB);
    b = ii(b, c, d, a, x[k + 9], S44, 0xEB86D391);
    a = addUnsigned(a, AA);
    b = addUnsigned(b, BB);
    c = addUnsigned(c, CC);
    d = addUnsigned(d, DD);
  }

  return (wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d)).toLowerCase();
};

const HashGenerator = () => {
  // Text Hash State
  const [textInput, setTextInput] = useState('');
  const [textHashes, setTextHashes] = useState({
    'SHA-256': '',
    'SHA-512': '',
    'SHA-1': '',
    'MD5': ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  
  // File Hash State
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileHashes, setFileHashes] = useState({
    'SHA-256': '',
    'SHA-512': '',
    'SHA-1': '',
    'MD5': ''
  });
  const [isFileHashing, setIsFileHashing] = useState(false);
  
  // Verify Hash State
  const [verifyHash, setVerifyHash] = useState('');
  const [verifyInput, setVerifyInput] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [detectedAlgorithm, setDetectedAlgorithm] = useState('');
  
  const fileInputRef = useRef(null);

  // Generate hashes for text
  const generateTextHashes = async () => {
    if (!textInput.trim()) {
      toast.error('Please enter some text');
      return;
    }
    
    setIsGenerating(true);
    try {
      const hashes = {
        'SHA-256': await generateHash(textInput, 'SHA-256'),
        'SHA-512': await generateHash(textInput, 'SHA-512'),
        'SHA-1': await generateHash(textInput, 'SHA-1'),
        'MD5': await generateHash(textInput, 'MD5')
      };
      setTextHashes(hashes);
      toast.success('Hashes generated successfully!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate hashes');
    }
    setIsGenerating(false);
  };

  // Generate hashes for file
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    setIsFileHashing(true);
    setFileHashes({ 'SHA-256': '', 'SHA-512': '', 'SHA-1': '', 'MD5': '' });
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      const hashes = {
        'SHA-256': await generateHash(uint8Array, 'SHA-256'),
        'SHA-512': await generateHash(uint8Array, 'SHA-512'),
        'SHA-1': await generateHash(uint8Array, 'SHA-1'),
        'MD5': await generateHash(uint8Array, 'MD5')
      };
      
      setFileHashes(hashes);
      toast.success('File hashes generated!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to hash file');
    }
    setIsFileHashing(false);
  };

  // Detect hash algorithm by length
  const detectAlgorithm = (hash) => {
    const cleanHash = hash.trim().toLowerCase();
    switch (cleanHash.length) {
      case 32: return 'MD5';
      case 40: return 'SHA-1';
      case 64: return 'SHA-256';
      case 128: return 'SHA-512';
      default: return 'Unknown';
    }
  };

  // Verify hash
  const verifyHashMatch = async () => {
    if (!verifyHash.trim() || !verifyInput.trim()) {
      toast.error('Please enter both hash and text to verify');
      return;
    }
    
    const cleanHash = verifyHash.trim().toLowerCase();
    const algorithm = detectAlgorithm(cleanHash);
    setDetectedAlgorithm(algorithm);
    
    if (algorithm === 'Unknown') {
      toast.error('Could not detect hash algorithm');
      setVerifyResult(null);
      return;
    }
    
    try {
      const computedHash = await generateHash(verifyInput, algorithm);
      const isMatch = computedHash === cleanHash;
      setVerifyResult(isMatch);
      
      if (isMatch) {
        toast.success('Hash verified! The content matches.');
      } else {
        toast.error('Hash mismatch! The content does not match.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Verification failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Hash copied to clipboard!');
  };

  const HashDisplay = ({ algorithm, hash, loading }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-white font-medium">{algorithm}</Label>
        <span className="text-xs text-gray-400">
          {algorithm === 'SHA-256' && '64 characters'}
          {algorithm === 'SHA-512' && '128 characters'}
          {algorithm === 'SHA-1' && '40 characters'}
          {algorithm === 'MD5' && '32 characters'}
        </span>
      </div>
      <div className="flex gap-2">
        <Input
          readOnly
          value={loading ? 'Calculating...' : hash}
          placeholder={`${algorithm} hash will appear here`}
          className="font-mono text-xs bg-gray-900 border-gray-600 text-cyan-400"
          data-testid={`hash-${algorithm.toLowerCase()}`}
        />
        <Button
          onClick={() => copyToClipboard(hash)}
          variant="outline"
          size="icon"
          className="border-gray-600 hover:bg-gray-700 flex-shrink-0"
          disabled={!hash}
          data-testid={`copy-hash-${algorithm.toLowerCase()}`}
        >
          <Copy className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
          Hash Generator
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Generate and verify file hashes using SHA-256, SHA-512, SHA-1, and MD5 algorithms locally.
        </p>
      </div>

      <Tabs defaultValue="text" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[600px] mx-auto mb-8 bg-gray-800/50">
          <TabsTrigger value="text" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-text-hash">
            <FileText className="w-4 h-4" />
            Text Hash
          </TabsTrigger>
          <TabsTrigger value="file" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-file-hash">
            <Upload className="w-4 h-4" />
            File Hash
          </TabsTrigger>
          <TabsTrigger value="verify" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-verify-hash">
            <Shield className="w-4 h-4" />
            Verify Hash
          </TabsTrigger>
        </TabsList>

        {/* Text Hash Tab */}
        <TabsContent value="text" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Input Text
                </CardTitle>
                <CardDescription className="text-gray-400">Enter text to generate hashes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Enter your text here..."
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white"
                  data-testid="text-hash-input"
                />
                
                <Button 
                  onClick={generateTextHashes}
                  disabled={isGenerating}
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  data-testid="generate-text-hash-btn"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Hash className="w-4 h-4 mr-2" />
                      Generate All Hashes
                    </>
                  )}
                </Button>
                
                <Alert className="bg-blue-900/30 border-blue-600/50">
                  <Info className="w-4 h-4 text-blue-400" />
                  <AlertDescription className="text-blue-200">
                    All hashing is performed locally in your browser. Your data never leaves your device.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Hash className="w-5 h-5 text-purple-400" />
                  Generated Hashes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <HashDisplay algorithm="SHA-256" hash={textHashes['SHA-256']} loading={isGenerating} />
                <HashDisplay algorithm="SHA-512" hash={textHashes['SHA-512']} loading={isGenerating} />
                <HashDisplay algorithm="SHA-1" hash={textHashes['SHA-1']} loading={isGenerating} />
                <HashDisplay algorithm="MD5" hash={textHashes['MD5']} loading={isGenerating} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* File Hash Tab */}
        <TabsContent value="file" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Upload className="w-5 h-5 text-cyan-400" />
                  Select File
                </CardTitle>
                <CardDescription className="text-gray-400">Choose a file to calculate its hashes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-cyan-400 transition-colors"
                >
                  {isFileHashing ? (
                    <div className="space-y-4">
                      <RefreshCw className="w-12 h-12 mx-auto text-cyan-400 animate-spin" />
                      <p className="text-white font-medium">Calculating hashes...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-white font-medium">Click to select a file</p>
                      <p className="text-gray-400 text-sm mt-1">or drag and drop</p>
                      {selectedFile && (
                        <div className="mt-4 p-3 bg-gray-900 rounded-lg">
                          <p className="text-cyan-400 font-medium">{selectedFile.name}</p>
                          <p className="text-gray-400 text-sm">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-hash-input"
                />
                
                <Alert className="bg-amber-900/30 border-amber-600/50">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <AlertDescription className="text-amber-200">
                    Large files may take longer to process. All processing happens in your browser.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Hash className="w-5 h-5 text-purple-400" />
                  File Hashes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <HashDisplay algorithm="SHA-256" hash={fileHashes['SHA-256']} loading={isFileHashing} />
                <HashDisplay algorithm="SHA-512" hash={fileHashes['SHA-512']} loading={isFileHashing} />
                <HashDisplay algorithm="SHA-1" hash={fileHashes['SHA-1']} loading={isFileHashing} />
                <HashDisplay algorithm="MD5" hash={fileHashes['MD5']} loading={isFileHashing} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Verify Hash Tab */}
        <TabsContent value="verify" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  Verify Hash
                </CardTitle>
                <CardDescription className="text-gray-400">Check if content matches a known hash</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Known Hash</Label>
                  <Textarea
                    placeholder="Paste the hash you want to verify..."
                    value={verifyHash}
                    onChange={(e) => {
                      setVerifyHash(e.target.value);
                      setVerifyResult(null);
                      setDetectedAlgorithm(detectAlgorithm(e.target.value));
                    }}
                    className="bg-gray-900 border-gray-600 text-white font-mono"
                    rows={2}
                    data-testid="verify-hash-input"
                  />
                  {verifyHash && (
                    <p className="text-sm text-gray-400">
                      Detected algorithm: <span className="text-cyan-400">{detectAlgorithm(verifyHash)}</span>
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Content to Verify</Label>
                  <Textarea
                    placeholder="Enter the content to verify against the hash..."
                    value={verifyInput}
                    onChange={(e) => {
                      setVerifyInput(e.target.value);
                      setVerifyResult(null);
                    }}
                    className="min-h-[150px] bg-gray-900 border-gray-600 text-white"
                    data-testid="verify-content-input"
                  />
                </div>
                
                <Button 
                  onClick={verifyHashMatch}
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  data-testid="verify-hash-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Verify Hash
                </Button>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Verification Result
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 rounded-xl p-6 min-h-[200px] flex items-center justify-center border border-gray-700">
                  {verifyResult === null ? (
                    <div className="text-center text-gray-500">
                      <Shield className="w-16 h-16 mx-auto mb-4" />
                      <p className="font-medium">Enter a hash and content to verify</p>
                      <p className="text-sm mt-1">Result will appear here</p>
                    </div>
                  ) : verifyResult ? (
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-12 h-12 text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-500">Hash Verified!</h3>
                      <p className="text-gray-400 mt-2">The content matches the provided {detectedAlgorithm} hash</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                        <X className="w-12 h-12 text-red-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-red-500">Hash Mismatch!</h3>
                      <p className="text-gray-400 mt-2">The content does not match the provided {detectedAlgorithm} hash</p>
                    </div>
                  )}
                </div>
                
                <Alert className="bg-purple-900/30 border-purple-600/50">
                  <Info className="w-4 h-4 text-purple-400" />
                  <AlertDescription className="text-purple-200">
                    <strong>Hash lengths:</strong><br />
                    MD5 = 32 chars | SHA-1 = 40 chars<br />
                    SHA-256 = 64 chars | SHA-512 = 128 chars
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Info Section */}
      <Card className="border-gray-700 bg-gray-800/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Info className="w-5 h-5 text-cyan-400" />
            About Hash Functions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-gray-900 border border-gray-700">
              <h4 className="font-bold text-cyan-400 mb-2">SHA-256</h4>
              <p className="text-sm text-gray-400">
                Most commonly used. Provides 256-bit security. Standard for most applications.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-900 border border-gray-700">
              <h4 className="font-bold text-purple-400 mb-2">SHA-512</h4>
              <p className="text-sm text-gray-400">
                Maximum security with 512-bit output. Best for high-security applications.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-900 border border-gray-700">
              <h4 className="font-bold text-amber-400 mb-2">SHA-1</h4>
              <p className="text-sm text-gray-400">
                Legacy algorithm. Not recommended for security purposes. Good for checksums.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-900 border border-gray-700">
              <h4 className="font-bold text-red-400 mb-2">MD5</h4>
              <p className="text-sm text-gray-400">
                Fast but cryptographically broken. Use only for non-security checksums.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HashGenerator;
